import { useEffect, useMemo, useState } from 'react';
import { formatDate, formatMoney, type FinanceInvoice } from '@/lib/finance';
import type { CompanyProfile } from '@/lib/companyProfile';
import {
  buildReminderMessage,
  daysLate,
  isOverdue,
  logReminder,
  reminderMailto,
  reminderWhatsAppUrl,
  type PaymentChannel,
} from '@/lib/invoicePayments';

const AUTO_KEY = 'zifek_finance_auto_reminders';
const LOG_KEY = 'zifek_finance_reminder_log';
/** Délai minimal entre deux relances automatiques d'une même facture. */
const AUTO_DELAY_DAYS = 3;

type ReminderLog = Record<string, string>;

function readLog(): ReminderLog {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    return raw ? (JSON.parse(raw) as ReminderLog) : {};
  } catch {
    return {};
  }
}

function writeLog(log: ReminderLog) {
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(log));
  } catch {
    /* stockage indisponible */
  }
}

function daysSince(iso: string | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return (Date.now() - t) / 86400000;
}

/**
 * Centre de relances : regroupe les factures impayées, propose la relance
 * e-mail / WhatsApp en un clic avec message pré-rempli et gère un mode
 * automatique (une relance par facture tous les 3 jours).
 */
export default function InvoiceReminderModal({
  invoices,
  profile,
  merchantId,
  onClose,
  onDone,
}: {
  invoices: FinanceInvoice[];
  profile: CompanyProfile;
  merchantId: number | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [auto, setAuto] = useState(false);
  const [queueIndex, setQueueIndex] = useState<number | null>(null);
  const [sentCount, setSentCount] = useState(0);
  const [log, setLog] = useState<ReminderLog>();

  useEffect(() => {
    try {
      setAuto(localStorage.getItem(AUTO_KEY) === '1');
    } catch {
      setAuto(false);
    }
    setLog(readLog());
  }, []);

  const overdue = useMemo(
    () =>
      invoices
        .filter((i) => (i.doc_type || 'invoice') === 'invoice' && isOverdue(i))
        .sort((a, b) => daysLate(b) - daysLate(a)),
    [invoices],
  );

  // Mode automatique : uniquement les factures non relancées depuis 3 jours.
  const dueToday = useMemo(() => {
    if (!auto) return overdue;
    return overdue.filter((i) => daysSince(log[String(i.id)]) >= AUTO_DELAY_DAYS);
  }, [auto, overdue, log]);

  const toggleAuto = () => {
    const next = !auto;
    setAuto(next);
    try {
      localStorage.setItem(AUTO_KEY, next ? '1' : '0');
    } catch {
      /* stockage indisponible */
    }
    if (next) {
      const count = dueToday.length;
      onDone(
        count > 0
          ? `Relance automatique activée : ${count} facture(s) à relancer aujourd'hui.`
          : 'Relance automatique activée. Aucune facture à relancer aujourd’hui.',
      );
    }
  };

  const remainingOf = (i: FinanceInvoice) => Math.max(0, Number(i.total) - Number(i.amount_paid));

  const registerSend = async (invoice: FinanceInvoice, channel: PaymentChannel) => {
    const message = buildReminderMessage(invoice, profile);
    if (merchantId) {
      await logReminder({
        invoiceId: invoice.id,
        merchantId,
        channel,
        recipient: channel === 'email' ? invoice.customer_email : invoice.customer_phone,
        message,
      });
    }
    const next = { ...readLog(), [String(invoice.id)]: new Date().toISOString() };
    writeLog(next);
    setLog(next);
    setSentCount((n) => n + 1);
  };

  const sendEmail = async (invoice: FinanceInvoice) => {
    window.location.href = reminderMailto(invoice, profile);
    await registerSend(invoice, 'email');
  };

  const sendWhatsApp = async (invoice: FinanceInvoice) => {
    window.open(reminderWhatsAppUrl(invoice, profile), '_blank');
    await registerSend(invoice, 'whatsapp');
  };

  const current = queueIndex != null ? dueToday[queueIndex] : null;

  const advanceQueue = async (invoice: FinanceInvoice, channel: PaymentChannel) => {
    if (channel === 'email') await sendEmail(invoice);
    else await sendWhatsApp(invoice);
    const nextIndex = (queueIndex ?? 0) + 1;
    if (nextIndex >= dueToday.length) {
      setQueueIndex(null);
      onDone(`Relances envoyées : ${sentCount + 1} facture(s).`);
    } else {
      setQueueIndex(nextIndex);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[92vh] flex flex-col mx-0 sm:mx-4">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-background-200/70 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-mail-send-line text-primary-500"></i>
              Relances des factures impayées
            </h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {overdue.length} facture(s) en retard · message de rappel pré-rempli
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {/* Mode automatique */}
          <div className="flex items-start gap-3 p-3.5 rounded-lg bg-background-100 border border-background-200/70 mb-4">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${auto ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-400'}`}>
              <i className="ri-flashlight-line"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground-900">Relance automatique</p>
              <p className="text-[11px] text-foreground-500 leading-snug mt-0.5">
                Prépare une relance e-mail + WhatsApp par facture impayée, tous les 3 jours, sans doublon.
              </p>
            </div>
            <button
              onClick={toggleAuto}
              role="switch"
              aria-checked={auto}
              className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors cursor-pointer ${auto ? 'bg-primary-500' : 'bg-background-300/70'}`}
              title={auto ? 'Désactiver' : 'Activer'}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${auto ? 'left-[22px]' : 'left-0.5'}`}></span>
            </button>
          </div>

          {current ? (
            <div className="rounded-lg border border-primary-200/70 bg-primary-50/40 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-700 mb-2">
                Relance {(queueIndex ?? 0) + 1} / {dueToday.length}
              </p>
              <p className="text-sm font-bold text-foreground-950">{current.number} · {current.customer_name || 'Client'}</p>
              <p className="text-xs text-foreground-500 mt-1">
                Reste {formatMoney(remainingOf(current), current.currency || 'MAD')} · retard de {daysLate(current)} jour(s)
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => advanceQueue(current, 'email')}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-mail-send-line"></i>Relancer par e-mail
                </button>
                <button
                  onClick={() => advanceQueue(current, 'whatsapp')}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>Relancer sur WhatsApp
                </button>
                <button
                  onClick={() => {
                    const nextIndex = (queueIndex ?? 0) + 1;
                    if (nextIndex >= dueToday.length) {
                      setQueueIndex(null);
                    } else {
                      setQueueIndex(nextIndex);
                    }
                  }}
                  className="px-3.5 py-2 rounded-full border border-background-200/70 text-foreground-600 text-xs font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
                >
                  Passer
                </button>
              </div>
            </div>
          ) : overdue.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-3">
                <i className="ri-check-double-line text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-800">Aucune facture en retard</p>
              <p className="text-xs text-foreground-500 mt-1">Toutes vos factures sont réglées ou pas encore échues.</p>
            </div>
          ) : (
            <>
              {auto && (
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-foreground-500">
                    {dueToday.length} facture(s) à relancer aujourd’hui
                  </p>
                  {dueToday.length > 0 && (
                    <button
                      onClick={() => setQueueIndex(0)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-play-line"></i>Tout relancer
                    </button>
                  )}
                </div>
              )}
              <div className="divide-y divide-background-200/70 border border-background-200/70 rounded-lg overflow-hidden">
                {overdue.map((inv) => {
                  const lastSent = log[String(inv.id)];
                  return (
                    <div key={inv.id} className="flex flex-wrap items-center gap-2.5 px-3.5 py-3">
                      <span className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-warning-line text-sm"></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-900 truncate">
                          {inv.number} · {inv.customer_name || 'Client'}
                        </p>
                        <p className="text-[11px] text-foreground-400">
                          Échéance {formatDate(inv.due_date)} · retard {daysLate(inv)} j
                          {lastSent ? ` · relancée le ${new Date(lastSent).toLocaleDateString('fr-FR')}` : ' · jamais relancée'}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                        {formatMoney(remainingOf(inv), inv.currency || 'MAD')}
                      </span>
                      <button
                        onClick={() => sendEmail(inv)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                        title="Relancer par e-mail (message pré-rempli)"
                      >
                        <i className="ri-mail-send-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => sendWhatsApp(inv)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                        title="Relancer sur WhatsApp"
                      >
                        <i className="ri-whatsapp-line text-sm"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <p className="mt-3 text-[11px] text-foreground-400 flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5"></i>
            La relance s’ouvre dans votre messagerie ou WhatsApp avec le montant restant dû, l’échéance et le nombre de jours de retard. Chaque envoi est enregistré pour éviter les doublons.
          </p>
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-background-200/70 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full border border-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}